import React from 'react';
import { Ellipsis } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { cn } from '@cotera/client/v0/lib/utils';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';
import type { ItemAction } from './resource-list-view-types';

const SubmenuAction: React.FC<{ action: ItemAction }> = ({ action }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <DropdownMenuSub open={open} onOpenChange={setOpen}>
      <DropdownMenuSubTrigger
        className={cn(
          'cursor-pointer',
          action.variant === 'destructive' &&
            'text-destructive focus:text-destructive'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* `DropdownMenuSubTrigger` takes no `variant`, so the icon has to be
            reddened by hand here — its own `svg:not([class*='text-'])` rule
            would otherwise mute the glyph beside an already-red label. No
            submenu action is destructive today; this keeps the first one that
            is from having to discover the rule. */}
        {action.icon !== undefined ? (
          <Icon
            icon={action.icon}
            className={
              action.variant === 'destructive' ? 'text-destructive' : undefined
            }
          />
        ) : null}
        {action.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        onClick={(e) => e.stopPropagation()}
        sideOffset={0}
        className="p-0"
      >
        {action.submenu?.(() => setOpen(false))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

/**
 * The `…` row-actions menu — one implementation for every list row in the app.
 *
 * There were three near-copies of this before: one here paired with a
 * (since-removed) leading-icon variant, and a private generic one inside
 * `resource-list-view-virtual-item.tsx` with a byte-identical `SubmenuAction`
 * beside it. They rendered at different sizes, and for a while this took
 * `triggerClassName`/`iconClassName` so both appearances could be preserved
 * through the merge — which is how the Library came to draw a 24px glyph beside
 * Automations' 16px one, and its own grid cards a third size again. The trigger
 * is now fixed here and those hooks are gone: one control, one size, everywhere.
 * `contentClassName` survives because menu width really is content-driven — the
 * Library's labels are shorter than the Automations rows'.
 *
 * Callers own placement and reveal, and must render this OUTSIDE any enclosing
 * `<button>`/`<a>` (nested interactive elements are invalid HTML) and above any
 * stretched `before:inset-0` hit area, or the trigger won't take the click.
 */
export const RowActionsMenu: React.FC<{
  actions: ItemAction[];
  /** Fired when the menu opens (e.g. to target the row's selection model). */
  onOpen?: () => void;
  label?: string;
  align?: 'start' | 'end';
  contentClassName?: string;
}> = ({
  actions,
  onOpen,
  label = 'Row actions',
  align = 'end',
  contentClassName = 'w-44',
}) => {
  if (actions.length === 0) {
    return null;
  }
  return (
    <DropdownMenu
      // Non-modal so closing the menu doesn't leave Radix's modal
      // `pointer-events: none` lock on the rest of the page (and doesn't trap
      // focus inside the menu). Several items here open a modal (rename,
      // archive, …); with the default modal menu that lock raced the modal as it
      // opened — eating the first click on a confirm button and keeping our
      // ModalPageView focus-reclaim from settling the caret in a text field, so
      // users had to click the modal once before it responded. Keyboard use is
      // unaffected: Radix still drives arrow-key navigation between items and
      // Escape-to-close returns focus to this trigger.
      modal={false}
      onOpenChange={(open) => {
        if (open) {
          onOpen?.();
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary focus:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:bg-secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Ellipsis className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={contentClassName}>
        {actions.map((action) =>
          action.submenu !== undefined ? (
            <SubmenuAction key={action.label} action={action} />
          ) : (
            <DropdownMenuItem
              key={action.label}
              // `variant`, not a `text-destructive` class: only the data
              // attribute it sets also reddens the leading icon and the focus
              // background, which is what the detail-page menus render.
              variant={action.variant}
              className="cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                action.onClick?.();
              }}
            >
              {action.icon !== undefined ? <Icon icon={action.icon} /> : null}
              {action.label}
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
